import { useState } from 'react';
import type { ValidationIssue } from '@royaltyflow/validation';
import { useAuth } from '../../api/auth';
import {
  useDeleteSimulation,
  useApproveReport,
  useRunSimulation,
  useSimulation,
} from '../../api/hooks';
import type { SimulationView } from '../../api/types';
import { Badge, Button, FormField, useToast } from '../../components/DesignSystem';
import { ValidationIssuePanel, validationIssues } from '../../components/ValidationIssuePanel';
import { errorMessage } from '../../lib/errors';
import { formatCurrency, formatDate } from '../../lib/format';
import { LevelDeductionsTable } from './LevelDeductionsTable';

/** The calculation workspace, displayed on its own route. */
export function SimulationCard({
  simulation,
  onDeleted,
}: {
  simulation: SimulationView;
  onDeleted?: () => void;
}) {
  const detail = useSimulation(simulation.id);
  const current = detail.data ?? simulation;
  const { token } = useAuth();
  const remove = useDeleteSimulation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const run = useRunSimulation();
  const approve = useApproveReport();
  const { showToast } = useToast();
  const [periodStart, setStart] = useState(simulation.periodStart ?? '');
  const [periodEnd, setEnd] = useState(simulation.periodEnd ?? '');
  const [partyId, setPartyId] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const busy = remove.isPending || run.isPending || approve.isPending || downloading;
  const errors = issues.length ? issues : (current.validationIssues ?? []);
  const sealed = current.status === 'approved' || current.status === 'archived';
  const dateChanged = periodStart !== current.periodStart || periodEnd !== current.periodEnd;
  const summary = current.resultsSummary;
  const ready =
    !!detail.data &&
    !detail.isFetching &&
    !errors.length &&
    !dateChanged &&
    (sealed || current.canApprove);
  async function handleDelete() {
    try {
      await remove.mutateAsync(current.id);
      showToast('Simulation deleted');
      onDeleted?.();
    } catch (error) {
      showToast(errorMessage(error, 'Could not delete simulation'), 'error');
    }
  }
  async function handleRun() {
    try {
      await run.mutateAsync({ simulationId: current.id, periodStart, periodEnd });
      setIssues([]);
      showToast('Simulation computed');
    } catch (error) {
      setIssues(validationIssues(error));
    }
  }
  async function handleApprove() {
    try {
      await approve.mutateAsync(current.id);
      setIssues([]);
      showToast('Report approved');
    } catch (error) {
      setIssues(validationIssues(error));
    }
  }
  async function downloadStatement() {
    setDownloading(true);
    try {
      const response = await fetch(`/simulations/${current.id}/statement/${partyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok)
        throw new Error('The statement could not be exported. Rerun the simulation and try again.');
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement-${current.id}-${partyId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not download statement', 'error');
    } finally {
      setDownloading(false);
    }
  }
  return (
    <div className="simulation-workspace">
      <section className="rfp-card simulation-controls" aria-labelledby="period-heading">
        <div className="rfp-card__head">
          <div>
            <p className="simulation-eyebrow">01 / REPORTING PERIOD</p>
            <h2 id="period-heading">Set the period</h2>
            <p className="rfp-card__caption">
              Choose which sales to include, then run the calculation.
            </p>
          </div>
          <Badge tone={sealed ? 'teal' : 'neutral'}>{current.status}</Badge>
        </div>
        <div className="simulation-toolbar">
          <FormField htmlFor={`start-${current.id}`} label="Reporting start">
            <input
              id={`start-${current.id}`}
              type="date"
              value={periodStart}
              disabled={sealed || busy}
              onChange={(event) => setStart(event.target.value)}
            />
          </FormField>
          <FormField htmlFor={`end-${current.id}`} label="Reporting end">
            <input
              id={`end-${current.id}`}
              type="date"
              value={periodEnd}
              disabled={sealed || busy}
              onChange={(event) => setEnd(event.target.value)}
            />
          </FormField>
          {!sealed && (
            <Button
              type="button"
              disabled={busy || !periodStart || !periodEnd || periodEnd < periodStart}
              onClick={handleRun}
            >
              {run.isPending ? 'Validating and calculating…' : 'Run simulation'}
            </Button>
          )}
        </div>
        <p className="simulation-run-note">
          {current.computedAt
            ? `Last calculated ${formatDate(current.computedAt)}`
            : 'Ready when you are. Results will appear below.'}{' '}
          · {current.currency}
        </p>
        {dateChanged && summary && (
          <p role="status">
            Dates changed. Run the simulation to update the results and enable export.
          </p>
        )}
        {!sealed && (
          <div>
            {confirmDelete ? (
              <div role="group" aria-label="Confirm simulation deletion">
                <p>
                  Delete this unfinished simulation? Sales uploads are kept. To correct sales,
                  return to Authorised contracts and import a replacement CSV.
                </p>
                <Button type="button" disabled={busy} onClick={handleDelete}>
                  Confirm delete
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                Delete simulation
              </Button>
            )}
          </div>
        )}
        <ValidationIssuePanel issues={errors} />
        {detail.isError && (
          <p role="alert">
            Simulation details could not be loaded. Approval and export are unavailable.
          </p>
        )}
      </section>
      {summary && (
        <>
          <section className="simulation-metrics" aria-label="Reporting period totals">
            {(
              [
                ['Gross revenue', summary.grossTotal],
                ['Net receipts', summary.netTotal],
                ['Total payable', summary.distributedTotal],
                ['Unallocated', summary.unallocatedTotal ?? 0],
              ] as const
            ).map(([label, value]) => (
              <div className="simulation-metric" key={label}>
                <span>{label}</span>
                <strong>{formatCurrency(value, current.currency)}</strong>
              </div>
            ))}
          </section>
          <section className="rfp-card simulation-allocations" aria-labelledby="levels-heading">
            <div className="rfp-card__head">
              <div>
                <p className="simulation-eyebrow">02 / LEVEL ALLOCATIONS</p>
                <h2 id="levels-heading">Follow the money, level by level</h2>
                <p className="rfp-card__caption">
                  Start with net receipts. Subtract earlier entitlements for the same sale type,
                  territory and party. Apply this level’s share once.
                </p>
              </div>
            </div>
            <div className="simulation-toolbar simulation-export">
              <FormField htmlFor="statement-party" label="Party">
                <select
                  id="statement-party"
                  value={partyId}
                  disabled={busy}
                  onChange={(event) => setPartyId(event.target.value)}
                >
                  <option value="">All parties</option>
                  {summary.perParty.map((party) => (
                    <option key={party.partyId} value={party.partyId}>
                      {party.partyName}
                    </option>
                  ))}
                </select>
              </FormField>
              <Button
                variant="secondary"
                type="button"
                disabled={
                  busy || !ready || !summary.perParty.some((party) => party.partyId === partyId)
                }
                onClick={downloadStatement}
              >
                {downloading ? 'Preparing PDF…' : 'Download statement PDF'}
              </Button>
              <p className="rfp-card__caption">
                {partyId
                  ? 'A period summary and allocation detail for this party.'
                  : 'Select a party to export their statement.'}
                {!sealed && ' Draft until approved.'}
              </p>
            </div>
            {detail.data && <LevelDeductionsTable simulation={detail.data} partyId={partyId} />}
            <div className="simulation-payables">
              {summary.perParty
                .filter((party) => !partyId || party.partyId === partyId)
                .map((party) => (
                  <div key={party.partyId}>
                    <span>
                      Total due to <strong>{party.partyName}</strong>
                    </span>
                    <strong>{formatCurrency(party.total, current.currency)}</strong>
                  </div>
                ))}
            </div>
            <p className="rfp-card__caption">
              Total due includes applicable recoveries. The level table shows entitlements before
              deficit and advance recoupment.
            </p>
          </section>
        </>
      )}
      {!!summary?.recoupmentBalances?.length && (
        <section className="rfp-card" aria-labelledby="recoveries-heading">
          <h2 id="recoveries-heading">Advance, deficit and SUA balances</h2>
          <p className="rfp-card__caption">
            Earlier approved recoveries reduce the opening balance. This run only previews recovery;
            approval records it.
          </p>
          <div className="rfp-stage-scroll">
            <table className="rfp-stage-table">
              <caption>Recovery balances for this reporting period</caption>
              <thead>
                <tr>
                  <th>Party</th>
                  <th>Recovery</th>
                  <th>Scope</th>
                  <th>Original grant</th>
                  <th>Previously recovered</th>
                  <th>Opening balance</th>
                  <th>This period</th>
                  <th>Closing balance</th>
                </tr>
              </thead>
              <tbody>
                {summary.recoupmentBalances
                  .filter((pool) => !partyId || pool.partyId === partyId)
                  .map((pool) => (
                    <tr key={pool.poolId}>
                      <th scope="row">
                        {summary.perParty.find((party) => party.partyId === pool.partyId)
                          ?.partyName ?? 'Unassigned'}
                      </th>
                      <td>{pool.kind}</td>
                      <td>
                        {pool.calculationKey
                          ? `${pool.calculationKey.saleType} / ${pool.calculationKey.territory}`
                          : 'Party'}
                      </td>
                      {[
                        pool.originalAmount,
                        pool.previouslyApplied,
                        pool.openingBalance,
                        pool.movement,
                        pool.closingBalance,
                      ].map((amount, index) => (
                        <td key={index}>{formatCurrency(amount, current.currency)}</td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {!!detail.data?.stages.length && (
        <details className="rfp-card simulation-trace">
          <summary>View the full calculation breakdown</summary>
          <p>Opening, movement and closing balances, including costs and recoveries.</p>
          <div className="rfp-stage-scroll">
            <table className="rfp-stage-table">
              <caption>Calculation stages — opening, movement and closing balances</caption>
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Pool / basis</th>
                  <th>Opening</th>
                  <th>Movement</th>
                  <th>Closing</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.stages.map((stage) => (
                  <tr key={stage.sequence}>
                    <th scope="row">
                      {stage.label}
                      {stage.informationOnly && <small> · detail only</small>}
                    </th>
                    <td>
                      {stage.pool} / {stage.basis}
                    </td>
                    <td>{formatCurrency(stage.opening, current.currency)}</td>
                    <td>{formatCurrency(stage.movement, current.currency)}</td>
                    <td>{formatCurrency(stage.closing, current.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
      {!sealed && (
        <section className="rfp-card simulation-approval">
          <div>
            <p className="simulation-eyebrow">03 / REVIEW & APPROVE</p>
            <h2>Ready to finalise?</h2>
            <p className="rfp-card__caption">
              Approval seals this run. You can rerun and download draft statements before approving.
            </p>
          </div>
          <Button type="button" disabled={busy || !ready} onClick={handleApprove}>
            Approve report
          </Button>
        </section>
      )}
    </div>
  );
}
